import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Input, isBetweenMinAndMax, PillOverlayPopover } from '@utilities';

import styles from './_index.module.scss';

const NumericLogicInput = ({
  firstErrorRef,
  isError,
  isFocused,
  isRequired,
  question,
  rootCloseEvent,
  setValue,
  value,
}) => {
  const [isInputError, setIsInputError] = useState(isError);

  const minValue = question.properties?.minNumber;
  const maxValue = question.properties?.maxNumber;

  const numericValueError = useMemo(() => {
    if (minValue !== undefined && maxValue !== undefined) {
      return `Please enter a number between ${minValue} and ${maxValue}.`;
    }
    if (minValue !== undefined) {
      return `Please enter a number greater than or equal to ${minValue}.`;
    }
    if (maxValue !== undefined) {
      return `Please enter a number less than or equal to ${maxValue}.`;
    }
    return 'Please enter a number.';
  }, [minValue, maxValue]);

  const handleChange = useCallback(
    (inputValue) => {
      if (inputValue === '') {
        setIsInputError(false);
        setValue('');
        return;
      }

      const numericValue = parseFloat(inputValue);
      const isValid = isBetweenMinAndMax({ max: maxValue, min: minValue, value: numericValue });

      setIsInputError(!isValid);
      setValue(numericValue);
    },
    [maxValue, minValue, setValue]
  );

  return (
    <PillOverlayPopover
      buttonRef={isFocused ? firstErrorRef : null}
      isError={isError}
      isRequired={isRequired}
      overlayClassName={styles['numeric-input-overlay']}
      pillClassName={isFocused ? styles['numeric-input-pill-focus'] : null}
      rootCloseEvent={rootCloseEvent}
      value={value === '' ? 'Numerical Value' : value}
    >
      <Input
        className={styles['numeric-logic-input']}
        error={isError || isInputError ? numericValueError : null}
        id={`numeric-logic-input-${question.id}`}
        label="Enter a number"
        onChange={handleChange}
        placeholder="Numerical Value"
        type="number"
        value={value}
      />
    </PillOverlayPopover>
  );
};

NumericLogicInput.propTypes = {
  buttonRef: PropTypes.object,
  firstErrorRef: PropTypes.object,
  isError: PropTypes.bool,
  isFocused: PropTypes.bool,
  isRequired: PropTypes.bool,
  question: PropTypes.object.isRequired,
  rootCloseEvent: PropTypes.oneOf(['mousedown']),
  setValue: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default NumericLogicInput;
